/**
 * SpiritScanPublic - Scan Grátis na Landing Page
 * 
 * Permite fazer um KetoScan SEM registo para mostrar valor imediato.
 * Limita a 1 scan por sessão via localStorage.
 */

import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, Loader2, Check, AlertTriangle, Sparkles, Zap } from 'lucide-react';

const API = (typeof window !== 'undefined' && (window.location.hostname.endsWith('.emergent.host') || ['ketolab.pt', 'www.ketolab.pt', 'app.ketolab.pt', 'pro.ketolab.pt'].includes(window.location.hostname))) ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const SpiritScanPublic = ({ isOpen, onClose, onSignUp }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState('camera'); // camera, scanning, result
  const [image, setImage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        handleScan(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScan = async (imageData) => {
    setStep('scanning');
    setError(null);
    
    try {
      const response = await fetch(`${API}/api/ketoscan/public-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          language: i18n.language
        })
      });
      
      if (!response.ok) {
        throw new Error('Scan failed');
      }
      
      const data = await response.json();
      setResult(data);
      setStep('result');
      
      // Mark as used
      localStorage.setItem('spirit_scan_used', 'true');
      
    } catch (err) {
      console.error('Scan error:', err);
      setError(t('spiritScan.error'));
      setStep('camera');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'from-green-500/20 to-emerald-500/20 border-green-500/50';
    if (score >= 40) return 'from-yellow-500/20 to-amber-500/20 border-yellow-500/50';
    return 'from-red-500/20 to-orange-500/20 border-red-500/50';
  };

  const getIcon = (classification) => {
    if (classification === 'ideal') return <Check className="w-8 h-8 text-green-500" />;
    if (classification === 'adjusted') return <AlertTriangle className="w-8 h-8 text-yellow-500" />;
    return <X className="w-8 h-8 text-red-500" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#a3e635]" />
            <span className="font-bold text-white">{t('spiritScan.title')}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* CAMERA STEP */}
            {step === 'camera' && (
              <motion.div
                key="camera"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#a3e635]/20 to-emerald-500/20 flex items-center justify-center border border-[#a3e635]/50">
                  <Camera className="w-10 h-10 text-[#a3e635]" />
                </div>
                
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('spiritScan.cameraTitle')}
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  {t('spiritScan.cameraDesc')}
                </p>
                
                {error && (
                  <p className="text-red-500 text-sm mb-4">{error}</p>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-[#a3e635] text-black hover:bg-[#84cc16] font-bold"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {t('spiritScan.takePhoto')}
                </Button>
                
                <p className="text-xs text-gray-500 mt-4">
                  {t('spiritScan.free')}
                </p>
              </motion.div>
            )}

            {/* SCANNING STEP */}
            {step === 'scanning' && (
              <motion.div
                key="scanning"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#a3e635]/20 to-emerald-500/20 flex items-center justify-center animate-pulse">
                  <Loader2 className="w-10 h-10 text-[#a3e635] animate-spin" />
                </div>
                <p className="text-white font-medium">{t('spiritScan.analyzing')}</p>
                <p className="text-gray-400 text-sm mt-2">{t('spiritScan.wait')}</p>
              </motion.div>
            )}

            {/* RESULT STEP */}
            {step === 'result' && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {/* Score Card */}
                <div className={`p-5 rounded-2xl bg-gradient-to-br ${getScoreBg(result.keto_score)} border`}>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                      {getIcon(result.keto_classification)}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-lg">{result.food_name}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`text-3xl font-black ${getScoreColor(result.keto_score)}`}>
                          {result.keto_score}
                        </span>
                        <span className="text-gray-400">/100</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tip */}
                <div className="p-4 bg-zinc-800/50 rounded-xl">
                  <p className="text-gray-300 text-sm">
                    <Zap className="w-4 h-4 inline mr-1 text-[#a3e635]" />
                    {result.short_tip}
                  </p>
                </div>

                {/* CTA */}
                <div className="pt-2">
                  <p className="text-center text-gray-400 text-sm mb-3">
                    {result.cta_message}
                  </p>
                  <Button
                    onClick={onSignUp}
                    className="w-full bg-[#a3e635] text-black hover:bg-[#84cc16] font-bold"
                  >
                    {t('spiritScan.tryFree')}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpiritScanPublic;
